/**
 * LevelSelector Component
 * 
 * A reusable component for selecting learner levels including:
 * - Dropdown selection for learner levels (Beginner, Intermediate, Advanced)
 * - Loading state handling
 * - Error handling and validation
 * - Consistent styling and behavior across all content types
 * - Optional chip display mode for showing current level
 * 
 * Used in: Content creation and editing forms across the application
 */
import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Chip,
    Box,
    CircularProgress
} from '@mui/material';
import { userRequest } from '../../requestMethods';
import PropTypes from 'prop-types';

const LevelSelector = ({ 
    value, 
    onChange, 
    error, 
    helperText, 
    required = false, 
    disabled = false,
    size = 'medium',
    fullWidth = true,
    displayMode = 'select', // 'select' or 'chip'
    label = 'Learner Level'
}) => {
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');

    useEffect(() => {
        fetchLevels();
    }, []);

    const fetchLevels = async () => {
        setLoading(true);
        setFetchError('');
        try {
            // Note: We'll need to create this endpoint or use existing level data
            // For now, we'll use hardcoded levels that match the backend model
            const hardcodedLevels = [
                { _id: 'beginner', name: 'Beginner', sortOrder: 1 },
                { _id: 'intermediate', name: 'Intermediate', sortOrder: 2 },
                { _id: 'advanced', name: 'Advanced', sortOrder: 3 }
            ];
            
            // Try to fetch from API, fall back to hardcoded if needed
            try {
                const response = await userRequest.get('/learnerLevel');
                setLevels(response.data || hardcodedLevels);
            } catch (apiError) {
                console.warn('Could not fetch levels from API, using hardcoded levels:', apiError);
                setLevels(hardcodedLevels);
            }
        } catch (error) {
            console.error('Error loading learner levels:', error);
            setFetchError('Failed to load learner levels');
            // Fallback to hardcoded levels
            setLevels([
                { _id: 'beginner', name: 'Beginner', sortOrder: 1 },
                { _id: 'intermediate', name: 'Intermediate', sortOrder: 2 },
                { _id: 'advanced', name: 'Advanced', sortOrder: 3 }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getLevelName = (levelId) => {
        const level = levels.find(l => l._id === levelId || l.name.toLowerCase() === levelId?.toLowerCase());
        return level?.name || levelId || 'Unknown';
    };

    const getLevelColor = (levelId) => {
        const levelName = getLevelName(levelId)?.toLowerCase();
        switch (levelName) {
            case 'beginner':
                return 'success';
            case 'intermediate':
                return 'warning';
            case 'advanced':
                return 'error';
            default:
                return 'default';
        }
    };

    if (displayMode === 'chip' && value) {
        return (
            <Chip 
                label={getLevelName(value)}
                color={getLevelColor(value)}
                size="small"
                variant="outlined"
            />
        );
    }

    return (
        <FormControl 
            fullWidth={fullWidth} 
            error={!!error || !!fetchError}
            size={size}
            disabled={disabled || loading}
        >
            <InputLabel required={required}>
                {label}
                {loading && (
                    <CircularProgress 
                        size={12} 
                        sx={{ ml: 1 }} 
                        color="inherit"
                    />
                )}
            </InputLabel>
            <Select
                value={levels.length === 0 ? '' : (levels.some(l => l._id === value) ? value : '')}
                onChange={onChange}
                label={label}
                required={required}
                disabled={disabled || loading}
            >
                {loading ? (
                    <MenuItem value="" disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            Loading levels...
                        </Box>
                    </MenuItem>
                ) : levels.length === 0 ? (
                    <MenuItem value="" disabled>
                        No levels available
                    </MenuItem>
                ) : (
                    levels
                        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                        .map((level) => (
                            <MenuItem key={level._id} value={level._id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Chip 
                                        label={level.name}
                                        color={getLevelColor(level._id)}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            </MenuItem>
                        ))
                )}
            </Select>
            {(error || helperText || fetchError) && (
                <Typography 
                    variant="caption" 
                    color={error || fetchError ? "error" : "text.secondary"} 
                    sx={{ ml: 2, mt: 0.5 }}
                >
                    {error || fetchError || helperText}
                </Typography>
            )}
        </FormControl>
    );
};

LevelSelector.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
    fullWidth: PropTypes.bool,
    displayMode: PropTypes.oneOf(['select', 'chip']),
    label: PropTypes.string
};

export default LevelSelector;
